import { useSelector } from "react-redux";
import {
  useGetBillToMappingListDataQuery,
  useUpdateBillToMappingTableHeadDataMutation,
  useDeleteBillToMappingMutation,
} from "../../../store/queries/Master";
import { updateConfig as globalUpdateConfig } from "../../../store/slices/Global";
import { updateConfig } from "../../../store/slices/BillToMapping/billToMappingSlice";
import { useEffect } from "react";
import { useDispatch } from "react-redux";
import { toast } from "react-toastify";
import { getBillToMappingData } from "./api";

const useBillToMappingList = () => {
  let paginationOptions = [
    { label: "20 Per page", value: 20 },
    { label: "40 Per page", value: 40 },
    { label: "60 Per page", value: 60 },
  ];

  const dispatch = useDispatch();

  const { tableFields, showEditModal } = useSelector((state) => state.global);
  const billToMappingState = useSelector((state) => state.billToMapping);
  const supplierIds = Array.isArray(billToMappingState.filter?.supplier)
    ? billToMappingState.filter.supplier
    : billToMappingState.filter?.supplier
    ? [billToMappingState.filter.supplier]
    : [];
  const shopIds = Array.isArray(billToMappingState.filter?.shop)
    ? billToMappingState.filter.shop
    : billToMappingState.filter?.shop
    ? [billToMappingState.filter.shop]
    : [];
  const billToIds = Array.isArray(billToMappingState.filter?.bill_to)
    ? billToMappingState.filter.bill_to
    : billToMappingState.filter?.bill_to
    ? [billToMappingState.filter.bill_to]
    : [];
  const {
    data: billToMappingList = {},
    isLoading,
    isFetching,
    refetch,
  } = useGetBillToMappingListDataQuery({
    page_size: billToMappingState.currentPageSize,
    page: billToMappingState.currentPage,
    sort_by: billToMappingState.sortBy,
    sort_order: billToMappingState.sortOrder,
    search: billToMappingState.search,
    supplier_id: supplierIds,
    shop_id: shopIds,
    bill_to_id: billToIds,
  });

  const [deleteBillToMappingMutation] = useDeleteBillToMappingMutation();
  const [updateBillToMappingFields] =
    useUpdateBillToMappingTableHeadDataMutation();

  useEffect(() => {
    dispatch(
      globalUpdateConfig((state) => {
        state.tableFields = billToMappingList?.data?.fields;
      })
    );
    // eslint-disable-next-line
  }, [billToMappingList]);

  useEffect(() => {
    if (billToMappingState.clearSelection) {
      dispatch(
        updateConfig((state) => {
          state.clearSelection = false;
        })
      );
    }
    // eslint-disable-next-line
  }, [billToMappingState.clearSelection]);

  useEffect(() => {
    if (
      billToMappingState?.selectedBillToMapping &&
      billToMappingState?.isEdit
    ) {
      fillBillToMappingData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [billToMappingState?.isEdit, billToMappingState?.showAddBillToMappingModal]);

  const handleModalClick = () => {
    dispatch(
      updateConfig((state) => {
        state.showAddBillToMappingModal = !state.showAddBillToMappingModal;
        state.billToMappingData = [];
        state.isEdit = false;
      })
    );
  };

  const getRow = (feild, data) => {
    const rows = {
      common: (feild, data) => <p className="pro-mb-0">{data[feild]}</p>,
    };
    return rows?.[feild]?.(feild, data) ?? rows["common"](feild, data);
  };

  const handleDelete = () => {
    deleteBillToMappingMutation(billToMappingState?.selectedBillToMapping).then(
      (response) => {
        if (response?.data?.status_code === 200) {
          toast.success("Bill To deleted successfully");
          handleDeleteModal();
          refetch();
        } else {
          toast.error("Something went wrong");
          handleDeleteModal();
        }
      }
    );
  };

  const fillBillToMappingData = () => {
    getBillToMappingData(billToMappingState?.selectedBillToMapping).then(
      (response) => {
        if (response?.data?.status_code === 200) {
          dispatch(
            updateConfig(
              (state) => (state.billToMappingData = response?.data?.data)
            )
          );
        } else {
          toast.error("Something went wrong to fetch data");
        }
      }
    );
  };

  const handleDeleteModal = (e) => {
    dispatch(
      updateConfig((state) => {
        state.showDeleteModal = !state.showDeleteModal;
        state.selectedBillToMapping = e?.[0];
      })
    );
  };

  const handleEditAction = (e) => {
    dispatch(
      updateConfig((state) => {
        state.showAddBillToMappingModal = true;
        state.selectedBillToMapping = e?.[0];
        state.isEdit = true;
      })
    );
  };

  const handleSearch = (e) => {
    dispatch(
      updateConfig((state) => {
        state.search = e.target.value;
        state.currentPage = 1;
      })
    );
  };

  const handleEditColumnsClick = () => {
    dispatch(
      globalUpdateConfig((state) => {
        state.showEditModal = !state.showEditModal;
      })
    );
  };

  const handleClearClick = () => {
    dispatch(updateConfig((state) => (state.search = "")));
  };

  const handlePageSize = ({ value: page_size }) => {
    dispatch(
      updateConfig((state) => {
        state.currentPageSize = page_size;
        state.currentPage = 1;
      })
    );
  };

  const handlePagination = (page) => {
    dispatch(
      updateConfig((state) => {
        state.currentPage = page;
      })
    );
  };

  const handleSort = (label) => {
    if (billToMappingState.sortBy === label) {
      dispatch(
        updateConfig((state) => {
          state.currentPage = 1;
          state.sortOrder =
            billToMappingState.sortOrder === "asc" ? "desc" : "asc";
        })
      );
    } else {
      dispatch(
        updateConfig((state) => {
          state.currentPage = 1;
          state.sortBy = label;
          state.sortOrder = "asc";
        })
      );
    }
  };

  const handleFilterClick = () => {
    dispatch(
      updateConfig((state) => {
        state.showFilter = !state.showFilter;
      })
    );
  };

  const activeFilterCount = [
    billToMappingState?.filter?.supplier,
    billToMappingState?.filter?.shop,
    billToMappingState?.filter?.bill_to,
  ].filter((value) =>
    Array.isArray(value)
      ? value.length > 0
      : value !== "" && value !== undefined && value !== null
  ).length;

  return {
    billToMappingState,
    showEditModal,
    tableFields,
    billToMappingList,
    isLoading,
    isFetching,
    paginationOptions,
    activeFilterCount,
    getRow,
    handleEditColumnsClick,
    handleSort,
    handlePagination,
    handleModalClick,
    handleDelete,
    handleDeleteModal,
    handleEditAction,
    handleSearch,
    updateBillToMappingFields,
    handleClearClick,
    handlePageSize,
    handleFilterClick,
    refetch,
  };
};

export default useBillToMappingList;
